import React, { useEffect, useState, useRef } from 'react';

import {
  Bell,
  X,
  Check,
  CheckCheck,
  ExternalLink,
  Mail,
  FileText,
  User,
  Briefcase,
  Calendar,
  AlertCircle,
  MessageSquare,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAuthToken } from '../../utils/auth';

interface Notification {
  _id: string;
  title: string;
  subtitle?: string;
  description?: string;
  type: string;
  source: string;
  urgency: string;
  isRead: boolean;
  createdAt: string;
  entityType?: string;
  entityId?: string;
  caseId?: string;
  clientId?: string;
  contractId?: string;
  actions?: Array<{
    label: string;
    actionType: string;
    targetUrl: string;
  }>;
  metadata?: {
    emailId?: string;
    messageId?: string;
    threadId?: string;
    conversationId?: string;
    topic?: string; // ✅ For FastLearn notifications
    scenarioId?: string; // ✅ For Game Theory notifications
    whiteboardId?: string; // ✅ For Whiteboard notifications
    taskId?: string; // ✅ For Task notifications
    documentId?: string; // ✅ For Document notifications
    [key: string]: any; // ✅ Allow any additional metadata
  };
}

// ✅ ENTERPRISE: System notification type (Gmail errors, etc.)
interface SystemNotification {
  id: string;
  message: string;
  type: 'error' | 'warning' | 'info';
  source: string;
  timestamp: Date;
}

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onMarkAsRead?: (id: string) => void;
  onMarkAllAsRead?: () => void;
  // ✅ ENTERPRISE: System notifications from services
  systemNotifications?: SystemNotification[];
  onDismissSystemNotification?: (id: string) => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  isOpen,
  onClose,
  onMarkAsRead,
  onMarkAllAsRead,
  systemNotifications = [],
  onDismissSystemNotification,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Fetch notifications
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();

      // Fetch recent notifications (both read and unread for better UX)
      const response = await fetch('/api/notifications?limit=20', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          // Sort by createdAt descending (newest first)
          const sorted = data.data.sort((a: Notification, b: Notification) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          });
          setNotifications(sorted);
        } else {
          console.warn('Unexpected notification data format:', data);
        }
      } else {
        console.error('Failed to fetch notifications:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`/api/notifications/${id}/read`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setNotifications(prev => prev.map(n => (n._id === id ? { ...n, isRead: true } : n)));
        onMarkAsRead?.(id);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch('/api/notifications/read-all', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        onMarkAllAsRead?.();
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Helper function to extract ID from string or object
  const extractId = (id: any): string | null => {
    if (!id) return null;
    if (typeof id === 'string') {
      // If it's already a string, return it
      return id;
    }
    if (typeof id === 'object') {
      // Handle MongoDB ObjectId or populated object
      // Try _id first (most common), then id, then toString()
      if (id._id) {
        return typeof id._id === 'string' ? id._id : String(id._id);
      }
      if (id.id) {
        return typeof id.id === 'string' ? id.id : String(id.id);
      }
      // For MongoDB ObjectId objects, toString() works
      if (typeof id.toString === 'function') {
        return id.toString();
      }
    }
    // Fallback: convert to string
    return String(id);
  };

  const getNotificationUrl = (notification: Notification): string | null => {
    // Debug logging
    console.log('🔍 [NotificationPanel] Getting URL for notification:', {
      title: notification.title,
      type: notification.type,
      entityType: notification.entityType,
      caseId: notification.caseId,
      entityId: notification.entityId,
      actions: notification.actions,
      fullNotification: notification,
    });

    // Priority 1: Use actions targetUrl if available
    if (notification.actions && notification.actions.length > 0) {
      let url = notification.actions[0].targetUrl;
      if (url && url !== '#' && url) {
        console.log('✅ [NotificationPanel] Using action URL:', url);
        // Ensure the URL is properly formatted for cases
        // Backend might send /cases/{id} but we need /cases/{id}/overview
        if (url.startsWith('/cases/')) {
          // Check if it's just /cases/{id} without a sub-route
          const caseMatch = url.match(/^\/cases\/([^\/\?]+)$/);
          if (caseMatch) {
            url = `/cases/${caseMatch[1]}/overview`;
            console.log('✅ [NotificationPanel] Fixed case URL to:', url);
          } else if (
            !url.includes('/overview') &&
            !url.includes('/documents') &&
            !url.includes('/tasks')
          ) {
            // If it has a sub-route but not overview, add overview
            url = `${url}/overview`;
          }
        }
        return url;
      }
    }

    // Priority 2: Check if caseId exists (for any notification type that has a case)
    if (notification.caseId) {
      const caseId = extractId(notification.caseId);
      if (caseId) {
        console.log('✅ [NotificationPanel] Using caseId:', caseId);
        return `/cases/${caseId}/overview`;
      }
    }

    // Priority 3: Navigate based on entityType and entityId
    const entityType = notification.entityType || notification.type;

    switch (entityType) {
      case 'email':
        // For emails, use query param with emailId
        if (notification.metadata?.emailId) {
          return `/email-v2?emailId=${notification.metadata.emailId}`;
        }
        return '/email-v2';

      case 'case':
      case 'urgent': // Deadline notifications use 'urgent' type but have caseId
        // Navigate to case workspace
        if (notification.entityId) {
          const caseId = extractId(notification.entityId);
          if (caseId) {
            return `/cases/${caseId}/overview`;
          }
        }
        // Fallback: check caseId again (in case entityId wasn't set)
        if (notification.caseId) {
          const caseId = extractId(notification.caseId);
          if (caseId) {
            return `/cases/${caseId}/overview`;
          }
        }
        return '/cases';

      case 'document':
      case 'documents':
        // Navigate to documents page with query param
        if (notification.entityId) {
          return `/documents?documentId=${notification.entityId}`;
        }
        return '/documents';

      case 'client':
      case 'clients':
        // ✅ Navigate to clients page with query param
        if (notification.clientId || notification.entityId) {
          const clientId = notification.clientId || notification.entityId;
          return `/clients?clientId=${clientId}`;
        }
        return '/clients';

      case 'contract':
        // Navigate to contract detail page (has dedicated route)
        if (notification.contractId || notification.entityId) {
          const contractId = notification.contractId || notification.entityId;
          return `/contracts/${contractId}`;
        }
        return '/contracts';

      case 'task':
      case 'tasks':
        // Navigate to tasks page with query param
        if (notification.entityId) {
          return `/tasks?taskId=${notification.entityId}`;
        }
        return '/tasks';

      case 'whiteboard':
        // Navigate to specific whiteboard
        if (notification.entityId) {
          return `/whiteboard/${notification.entityId}`;
        }
        return '/whiteboard';

      case 'fastlearn':
        // Navigate to fastlearn page with query param
        if (notification.metadata?.topic) {
          return `/fastlearn-v2?topic=${encodeURIComponent(notification.metadata.topic)}`;
        }
        return '/fastlearn-v2';

      case 'gametheory':
      case 'game-theory':
        // Navigate to game theory page with query param
        if (notification.metadata?.scenarioId) {
          return `/game-theory-v2?scenarioId=${notification.metadata.scenarioId}`;
        }
        return '/game-theory-v2';

      case 'message':
      case 'team':
        // For messages, could navigate to a messages/chat page if it exists
        if (notification.metadata?.conversationId) {
          return `/messages/${notification.metadata.conversationId}`;
        }
        return null; // No messages page yet

      case 'system':
      default:
        // For system notifications, check if there's a specific target
        if (notification.caseId) {
          const caseId = extractId(notification.caseId);
          if (caseId) {
            return `/cases/${caseId}/overview`;
          }
        }
        if (notification.entityId && notification.entityType === 'case') {
          const caseId = extractId(notification.entityId);
          if (caseId) {
            return `/cases/${caseId}/overview`;
          }
        }
        if (notification.clientId) {
          const clientId = extractId(notification.clientId);
          if (clientId) {
            return `/clients?clientId=${clientId}`;
          }
        }
        return null;
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    console.log('🖱️ [NotificationPanel] Notification clicked:', notification);

    if (!notification.isRead) {
      handleMarkAsRead(notification._id);
    }

    // Get the navigation URL
    const url = getNotificationUrl(notification);

    if (url) {
      console.log('🚀 [NotificationPanel] Navigating to:', url);
      // ✅ ENTERPRISE FIX: Pass state to trigger refresh when navigating to cases
      navigate(url, { state: { fromNotification: true, notificationId: notification._id } });
      onClose();
    } else {
      console.warn('⚠️ [NotificationPanel] No URL found for notification:', notification);
      // If no specific URL, just close the panel
      onClose();
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'documents':
      case 'document':
        return <FileText className="h-4 w-4" />;
      case 'case':
        return <Briefcase className="h-4 w-4" />;
      case 'clients':
      case 'client':
        return <User className="h-4 w-4" />;
      case 'tasks':
      case 'task':
        return <Check className="h-4 w-4" />;
      case 'team':
      case 'message':
        return <MessageSquare className="h-4 w-4" />;
      case 'system':
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'contract':
        return <FileText className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'Critical':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'High':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Medium':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-96 max-h-[600px] rounded-2xl border border-slate-200 bg-white shadow-xl z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-slate-700" />
          <h3 className="font-semibold text-slate-900">Notifications</h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-semibold">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              title="Mark all as read"
            >
              <CheckCheck className="h-4 w-4 text-slate-600" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
            title="Close"
          >
            <X className="h-4 w-4 text-slate-600" />
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center text-slate-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto mb-2"></div>
            <p className="text-sm">Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <Bell className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No notifications</p>
            <p className="text-sm text-slate-400 mt-1">You're all caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {/* ✅ ENTERPRISE: System Notifications (Gmail errors, etc.) */}
            {systemNotifications.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
                <div className="px-4 py-2 text-xs font-semibold text-red-700 dark:text-red-300 flex items-center gap-2">
                  <AlertCircle className="h-3.5 w-3.5" />
                  System Alerts
                </div>
                {systemNotifications.map(sysNotif => (
                  <div
                    key={sysNotif.id}
                    className="px-4 py-3 flex items-start gap-3 hover:bg-red-100/50 dark:hover:bg-red-900/30"
                  >
                    <div
                      className={`mt-0.5 p-2 rounded-lg ${
                        sysNotif.type === 'error'
                          ? 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400'
                          : sysNotif.type === 'warning'
                            ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400'
                            : 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                      }`}
                    >
                      <AlertCircle className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-red-600 dark:text-red-400">
                          {sysNotif.source}
                        </span>
                        <span className="text-xs text-slate-400 dark:text-neutral-500">
                          {new Date(sysNotif.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 dark:text-neutral-300">
                        {sysNotif.message}
                      </p>
                    </div>
                    {onDismissSystemNotification && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          onDismissSystemNotification(sysNotif.id);
                        }}
                        className="p-1 hover:bg-red-200 dark:hover:bg-red-800/50 rounded transition-colors"
                      >
                        <X className="h-3.5 w-3.5 text-red-500" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Regular Notifications */}
            {notifications.map(notification => (
              <div
                key={notification._id}
                onClick={() => handleNotificationClick(notification)}
                className={`p-4 hover:bg-slate-50 transition-colors cursor-pointer ${
                  !notification.isRead ? 'bg-amber-50/50' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div
                    className={`mt-0.5 p-2 rounded-lg ${
                      !notification.isRead
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4
                        className={`text-sm font-semibold ${
                          !notification.isRead ? 'text-slate-900' : 'text-slate-700'
                        }`}
                      >
                        {notification.title}
                      </h4>
                      {!notification.isRead && (
                        <div className="h-2 w-2 rounded-full bg-amber-500 flex-shrink-0 mt-1.5"></div>
                      )}
                    </div>

                    {notification.subtitle && (
                      <p className="text-xs text-slate-600 mb-1 line-clamp-2">
                        {notification.subtitle}
                      </p>
                    )}

                    <div className="flex items-center gap-2 mt-2">
                      <span
                        className={`px-2 py-0.5 rounded-md text-xs font-medium border ${getUrgencyColor(notification.urgency)}`}
                      >
                        {notification.urgency}
                      </span>
                      <span className="text-xs text-slate-400">
                        {formatTime(notification.createdAt)}
                      </span>
                      {notification.source && (
                        <span className="text-xs text-slate-400">• {notification.source}</span>
                      )}
                    </div>
                  </div>

                  {/* Mark as read button */}
                  {!notification.isRead && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleMarkAsRead(notification._id);
                      }}
                      className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors flex-shrink-0"
                      title="Mark as read"
                    >
                      <Check className="h-4 w-4 text-slate-500" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="p-3 border-t border-slate-200">
          <button
            onClick={() => {
              navigate('/notifications');
              onClose();
            }}
            className="w-full py-2 text-sm font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
